import React from 'react';
import { 
    LucideIcon,
    LucideProps,
    Trash2,           // kehricht
    Sofa,             // sperrgut
    FileText,         // papier
    Package,          // karton
    Leaf,             // bio/gruengut
    TreeDeciduous,    // laub
    TreePine,         // christbaum
    Scissors,         // haecksel
    AlertTriangle,    // sonderabfall
    Repeat,           // bringholtag
    Sprout,           // kompost
    Wine,             // glass
    Box,              // alu (small metal)
    Wrench,           // metal (large)
    Shirt,            // textile
    Coffee,           // coffee_capsules
    Droplet,          // pet
    Recycle,          // plastic
    Battery,          // batteries
    Monitor,          // electro
    Lightbulb,        // lights
    Droplets,         // oil
    Square,           // styrofoam
    Mountain,         // mineral
    Disc,             // cd_dvd
    Dog,              // kadaver
    HelpCircle        // fallback
} from 'lucide-react';

/**
 * All known waste type IDs from the database.
 * These are fixed, non-translatable identifiers.
 */
export type WasteTypeId = 
    | 'uuid_kehricht'
    | 'uuid_sperrgut'
    | 'uuid_papier'
    | 'uuid_karton'
    | 'uuid_bio'
    | 'uuid_laub'
    | 'uuid_christbaum'
    | 'uuid_haecksel'
    | 'uuid_sonderabfall'
    | 'uuid_bringholtag'
    | 'uuid_kompost'
    | 'uuid_glass'
    | 'uuid_alu'
    | 'uuid_metal'
    | 'uuid_textile'
    | 'uuid_coffee_capsules'
    | 'uuid_pet'
    | 'uuid_plastic'
    | 'uuid_batteries'
    | 'uuid_electro'
    | 'uuid_lights'
    | 'uuid_oil'
    | 'uuid_styrofoam'
    | 'uuid_mineral'
    | 'uuid_cd_dvd'
    | 'uuid_kadaver';

/**
 * Fallback presentation for each waste type.
 *
 * The database is authoritative for `icon` and `color` — see `resolveIcon` and
 * `resolveColor` below, which prefer the values on a WasteType row whenever the
 * caller has one. This map only covers the case where the row has not loaded
 * yet (or predates the icon/colour columns being set), so a calendar chip still
 * renders something recognisable instead of a question mark.
 */
interface WasteTypeConfig {
    color: string;
    icon: LucideIcon;
}

const WASTE_TYPE_CONFIG: Record<WasteTypeId, WasteTypeConfig> = {
    uuid_kehricht:        { color: '#808080', icon: Trash2 },
    uuid_sperrgut:        { color: '#FFB6C1', icon: Sofa },
    uuid_papier:          { color: '#4682B4', icon: FileText },
    uuid_karton:          { color: '#8B4513', icon: Package },
    uuid_bio:             { color: '#20B2AA', icon: Leaf },
    uuid_laub:            { color: '#FFD700', icon: TreeDeciduous },
    uuid_christbaum:      { color: '#228B22', icon: TreePine },
    uuid_haecksel:        { color: '#228B22', icon: Scissors },
    uuid_sonderabfall:    { color: '#DC143C', icon: AlertTriangle },
    uuid_bringholtag:     { color: '#FF8C00', icon: Repeat },
    uuid_kompost:         { color: '#8B4513', icon: Sprout },
    uuid_glass:           { color: '#06B6D4', icon: Wine },
    uuid_alu:             { color: '#94A3B8', icon: Box },
    uuid_metal:           { color: '#71717A', icon: Wrench },
    uuid_textile:         { color: '#F97316', icon: Shirt },
    uuid_coffee_capsules: { color: '#78716C', icon: Coffee },
    uuid_pet:             { color: '#8B5CF6', icon: Droplet },
    uuid_plastic:         { color: '#A855F7', icon: Recycle },
    uuid_batteries:       { color: '#EC4899', icon: Battery },
    uuid_electro:         { color: '#0EA5E9', icon: Monitor },
    uuid_lights:          { color: '#FBBF24', icon: Lightbulb },
    uuid_oil:             { color: '#854D0E', icon: Droplets },
    uuid_styrofoam:       { color: '#E5E7EB', icon: Square },
    uuid_mineral:         { color: '#A3A3A3', icon: Mountain },
    uuid_cd_dvd:          { color: '#C084FC', icon: Disc },
    uuid_kadaver:         { color: '#737373', icon: Dog },
};

// Default config for unknown waste types
// Hex rather than a CSS variable: callers append alpha suffixes (`${color}33`)
// to build gradients, which is only valid on a hex value.
const DEFAULT_CONFIG: WasteTypeConfig = {
    color: '#94A3B8',
    icon: HelpCircle,
};

/**
 * Normalize waste ID to the canonical uuid_ format.
 * Handles "uuid_kehricht", "kehricht", or "KEHRICHT" -> "uuid_kehricht"
 */
function normalizeWasteId(wasteId: string): WasteTypeId | null {
    const id = wasteId.toLowerCase().replace(/^uuid_/, '');
    const fullId = `uuid_${id}` as WasteTypeId;
    
    return fullId in WASTE_TYPE_CONFIG ? fullId : null;
}

/**
 * Get the configuration for a waste type ID.
 * Returns default config if waste ID is unknown.
 */
function getWasteConfig(wasteId: string): WasteTypeConfig {
    const normalizedId = normalizeWasteId(wasteId);
    return normalizedId ? WASTE_TYPE_CONFIG[normalizedId] : DEFAULT_CONFIG;
}

/**
 * Icon names as stored in `waste_types.icon`, mapped to the component that
 * draws them. Editing a waste type's icon in the admin panel picks a key from
 * this registry, so the database stays the source of truth while the drawing
 * stays in the front end (a React component cannot live in Postgres).
 */
const ICON_REGISTRY: Record<string, LucideIcon> = {
    delete: Trash2,
    weekend: Sofa,
    description: FileText,
    inventory_2: Package,
    eco: Leaf,
    compost: Sprout,
    yard: TreeDeciduous,
    park: TreePine,
    content_cut: Scissors,
    warning: AlertTriangle,
    swap_horiz: Repeat,
    local_bar: Wine,
    wine_bar: Wine,
    deployed_code: Box,
    construction: Wrench,
    checkroom: Shirt,
    coffee: Coffee,
    water_bottle: Droplet,
    recycling: Recycle,
    battery_full: Battery,
    devices: Monitor,
    lightbulb: Lightbulb,
    oil_barrel: Droplets,
    view_in_ar: Square,
    landscape: Mountain,
    album: Disc,
    pets: Dog,
    help_outline: HelpCircle,
};

/** The subset of a WasteType row this component needs. Accepts the full row. */
export interface WasteTypePresentation {
    waste_id: string;
    icon?: string;
    color?: string;
}

/**
 * Resolve the icon component: database value first, hardcoded fallback second.
 */
export function resolveIcon(wasteType?: WasteTypePresentation | null, wasteId?: string): LucideIcon {
    const fromDb = wasteType?.icon ? ICON_REGISTRY[wasteType.icon] : undefined;
    if (fromDb) return fromDb;
    return getWasteConfig(wasteType?.waste_id ?? wasteId ?? '').icon;
}

/**
 * Resolve the colour: database value first, hardcoded fallback second.
 */
export function resolveColor(wasteType?: WasteTypePresentation | null, wasteId?: string): string {
    if (wasteType?.color) return wasteType.color;
    return getWasteConfig(wasteType?.waste_id ?? wasteId ?? '').color;
}

/**
 * Get the hex color for a waste type by ID alone (no row available).
 */
export function getWasteColor(wasteId: string): string {
    return getWasteConfig(wasteId).color;
}

/**
 * Get CSS color variable for waste type (deprecated - use getWasteColor)
 * @deprecated Use getWasteColor instead for direct hex colors
 */
export const getWasteColorVar = getWasteColor;

/**
 * Get the Lucide icon component for a waste type by ID alone.
 */
export function getWasteIconComponent(wasteId: string): LucideIcon {
    return getWasteConfig(wasteId).icon;
}

/**
 * Check if a waste ID is valid/known
 */
export function isValidWasteId(wasteId: string): boolean {
    return normalizeWasteId(wasteId) !== null;
}

/**
 * Get all known waste type IDs
 */
export function getAllWasteTypeIds(): WasteTypeId[] {
    return Object.keys(WASTE_TYPE_CONFIG) as WasteTypeId[];
}

// Component props
export interface WasteIconProps extends Omit<LucideProps, 'ref'> {
    /** Waste type ID (e.g., "uuid_kehricht" or "kehricht") */
    wasteId: string;
    /**
     * The waste type row from the API. Pass it whenever the caller already has
     * it — the icon and colour then come from the database, so renaming or
     * recolouring a category in the admin panel takes effect everywhere.
     */
    wasteType?: WasteTypePresentation | null;
}

/**
 * WasteIcon - Renders the icon for a waste type in its configured colour,
 * preferring the values stored on the waste type row.
 */
const WasteIcon: React.FC<WasteIconProps> = ({
    wasteId,
    wasteType,
    size = 16,
    strokeWidth = 2,
    style,
    ...props
}) => {
    const icon = resolveIcon(wasteType, wasteId);
    const color = resolveColor(wasteType, wasteId);

    // createElement rather than <Icon />: the icon is selected from a static
    // registry, so its identity is stable, but rendering a capitalised local
    // as JSX reads to the linter (and to React's compiler) as defining a
    // component during render, which would reset state on every re-render.
    return React.createElement(icon, {
        size,
        strokeWidth,
        style: { color, ...style },
        ...props,
    });
};

export default WasteIcon;
